/**
 * Shareholder Table Component
 *
 * Displays all shareholders with their holdings, ownership %, and voting %.
 */

import { useState, Fragment, useRef, useEffect } from "react";
import {
  Users,
  Plus,
  Search,
  Building2,
  User,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Pencil,
  History,
  UserX,
  Mail,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useShareholdersSummary, useDeleteShareholder } from "@/hooks/use-share-book";
import { AddShareholderModal } from "./AddShareholderModal";
import { EditShareholderModal } from "./EditShareholderModal";
import { BulkImportShareholdersModal } from "./BulkImportShareholdersModal";
import { Modal } from "@/components/common/Modal";
import type { ShareholderType, ShareholderWithHoldings } from "@/lib/api";

interface ShareholderTableProps {
  companyId: string;
  canEdit?: boolean;
  /** Callback when user clicks to view transaction history for a shareholder */
  onViewHistory?: (shareholderId: string, shareholderName: string) => void;
}

export function ShareholderTable({ companyId, canEdit = true, onViewHistory }: ShareholderTableProps) {
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingShareholder, setEditingShareholder] = useState<ShareholderWithHoldings | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingShareholder, setDeactivatingShareholder] = useState<ShareholderWithHoldings | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data: shareholders = [], isLoading } = useShareholdersSummary(companyId);
  const deleteShareholder = useDeleteShareholder();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenId(null);
      }
    };

    if (menuOpenId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [menuOpenId]);

  // Filter shareholders
  const filteredShareholders = shareholders.filter((sh) => {
    if (!showInactive && !sh.isActive) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        sh.name.toLowerCase().includes(searchLower) ||
        sh.personalId?.toLowerCase().includes(searchLower) ||
        sh.orgNumber?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getTypeIcon = (type: ShareholderType) => {
    switch (type) {
      case "company":
        return Building2;
      default:
        return User;
    }
  };

  const handleEditShareholder = (shareholder: ShareholderWithHoldings) => {
    setMenuOpenId(null);
    setEditingShareholder(shareholder);
    setShowEditModal(true);
  };

  const handleViewHistory = (shareholderId: string, shareholderName: string) => {
    setMenuOpenId(null);
    if (onViewHistory) {
      onViewHistory(shareholderId, shareholderName);
    } else {
      toast.info("Transaktionshistorik kommer snart");
    }
  };

  const handleSendEmail = (_shareholderId: string, email?: string) => {
    setMenuOpenId(null);
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      toast.error("Ingen e-postadress registrerad");
    }
  };

  const handleDeactivateShareholder = (shareholder: ShareholderWithHoldings) => {
    setMenuOpenId(null);
    setDeactivatingShareholder(shareholder);
    setShowDeactivateModal(true);
  };

  const confirmDeactivate = async () => {
    if (!deactivatingShareholder) return;

    try {
      await deleteShareholder.mutateAsync({
        shareholderId: deactivatingShareholder.id,
        companyId,
      });
      toast.success("Aktieägaren har avaktiverats");
      setShowDeactivateModal(false);
      setDeactivatingShareholder(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte avaktivera aktieägaren";
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-1/3" />
          <div className="h-12 bg-gray-100 rounded" />
          <div className="h-12 bg-gray-100 rounded" />
          <div className="h-12 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Users className="text-gray-400" size={24} />
            <h2 className="text-lg font-bold text-gray-900">Aktieägare</h2>
            <span className="text-sm text-gray-500">({filteredShareholders.length})</span>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowBulkImportModal(true)}>
                <Upload size={18} className="mr-2" />
                Importera
              </Button>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus size={18} className="mr-2" />
                Lägg till aktieägare
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Sök på namn, personnummer eller organisationsnummer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Visa inaktiva
          </label>
        </div>

        {/* Table */}
        {filteredShareholders.length > 0 ? (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Aktieägare
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Antal aktier
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Ägarandel
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Röstandel
                  </th>
                  <th className="w-12 px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredShareholders.map((shareholder) => {
                  const Icon = getTypeIcon(shareholder.type);
                  const isExpanded = expandedId === shareholder.id;

                  return (
                    <Fragment key={shareholder.id}>
                      <tr
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          !shareholder.isActive ? "opacity-50" : ""
                        }`}
                      >
                        <td className="px-6 py-4">
                          <button
                            onClick={() => toggleExpand(shareholder.id)}
                            className="flex items-center gap-3 text-left w-full"
                          >
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon className="text-gray-600" size={20} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{shareholder.name}</p>
                              <p className="text-sm text-gray-500">
                                {shareholder.type === "company"
                                  ? shareholder.orgNumber
                                  : shareholder.hidePersonalId
                                    ? "●●●●●●-●●●●"
                                    : shareholder.personalId}
                              </p>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="text-gray-400" size={16} />
                            ) : (
                              <ChevronDown className="text-gray-400" size={16} />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-medium text-gray-900">
                            {shareholder.totalShares.toLocaleString("sv-SE")}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-medium text-gray-900">
                            {shareholder.ownershipPercent.toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-medium text-gray-900">
                            {shareholder.votingPercent.toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                if (menuOpenId === shareholder.id) {
                                  setMenuOpenId(null);
                                  setMenuPosition(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setMenuPosition({
                                    top: rect.bottom + 4,
                                    left: rect.right - 192, // 192px = w-48
                                  });
                                  setMenuOpenId(shareholder.id);
                                }
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            >
                              <MoreHorizontal size={18} />
                            </button>
                            {menuOpenId === shareholder.id && menuPosition && (
                              <div
                                ref={menuRef}
                                style={{ top: menuPosition.top, left: menuPosition.left }}
                                className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                {canEdit && (
                                  <button
                                    onClick={() => handleEditShareholder(shareholder)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <Pencil size={16} />
                                    Redigera
                                  </button>
                                )}
                                <button
                                  onClick={() => handleViewHistory(shareholder.id, shareholder.name)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <History size={16} />
                                  Transaktionshistorik
                                </button>
                                {shareholder.email && (
                                  <button
                                    onClick={() => handleSendEmail(shareholder.id, shareholder.email ?? undefined)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <Mail size={16} />
                                    Skicka e-post
                                  </button>
                                )}
                                {canEdit && (
                                  <>
                                    <div className="border-t border-gray-100 my-1" />
                                    <button
                                      onClick={() => handleDeactivateShareholder(shareholder)}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                      <UserX size={16} />
                                      Avaktivera
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={5} className="px-6 py-4">
                            <div className="ml-13">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Innehav per aktieslag</h4>
                              {shareholder.holdings.length > 0 ? (
                                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                  {shareholder.holdings.map((holding, idx) => (
                                    <div
                                      key={idx}
                                      className="p-3 bg-white border border-gray-200 rounded-lg"
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-gray-900">
                                          {holding.shareClass.name}
                                        </span>
                                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                          {holding.shareClass.code}
                                        </span>
                                      </div>
                                      <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Aktier:</span>
                                          <span className="text-gray-900">
                                            {holding.totalShares.toLocaleString("sv-SE")}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Röster:</span>
                                          <span className="text-gray-900">
                                            {holding.totalVotes.toLocaleString("sv-SE")}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Nummer:</span>
                                          <span className="text-gray-900 text-xs">
                                            {holding.shareNumbers}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">Inga innehav</p>
                              )}

                              {/* Contact info */}
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Kontaktuppgifter</h4>
                                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                                  {shareholder.address && (
                                    <div>
                                      <span className="text-gray-500">Adress: </span>
                                      <span className="text-gray-900">
                                        {shareholder.address}
                                        {shareholder.postalCode && `, ${shareholder.postalCode}`}
                                        {shareholder.city && ` ${shareholder.city}`}
                                      </span>
                                    </div>
                                  )}
                                  {shareholder.email && (
                                    <div>
                                      <span className="text-gray-500">E-post: </span>
                                      <span className="text-gray-900">{shareholder.email}</span>
                                    </div>
                                  )}
                                  {shareholder.phone && (
                                    <div>
                                      <span className="text-gray-500">Telefon: </span>
                                      <span className="text-gray-900">{shareholder.phone}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="mx-auto text-gray-300 mb-3" size={48} />
            <p className="text-gray-600 mb-4">
              {search ? "Inga aktieägare matchar din sökning" : "Inga aktieägare registrerade"}
            </p>
            {!search && canEdit && (
              <Button onClick={() => setShowAddModal(true)}>
                <Plus size={18} className="mr-2" />
                Lägg till aktieägare
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add Shareholder Modal */}
      {showAddModal && (
        <AddShareholderModal
          companyId={companyId}
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <BulkImportShareholdersModal
          companyId={companyId}
          isOpen={showBulkImportModal}
          onClose={() => setShowBulkImportModal(false)}
        />
      )}

      {/* Edit Shareholder Modal */}
      {showEditModal && editingShareholder && (
        <EditShareholderModal
          companyId={companyId}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingShareholder(null);
          }}
          shareholder={editingShareholder}
        />
      )}

      {/* Deactivate Shareholder Confirmation Modal */}
      {showDeactivateModal && deactivatingShareholder && (
        <Modal
          isOpen={showDeactivateModal}
          onClose={() => {
            setShowDeactivateModal(false);
            setDeactivatingShareholder(null);
          }}
          title="Avaktivera aktieägare"
          size="sm"
        >
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                Är du säker på att du vill avaktivera{" "}
                <strong>{deactivatingShareholder.name}</strong>?
              </p>
              {deactivatingShareholder.totalShares > 0 && (
                <p className="text-sm text-red-700 mt-2">
                  <strong>Varning:</strong> Aktieägaren har{" "}
                  {deactivatingShareholder.totalShares.toLocaleString("sv-SE")} aktier.
                  Överför aktierna till en annan ägare innan du avaktiverar.
                </p>
              )}
            </div>

            <p className="text-sm text-gray-600">
              Avaktiverade aktieägare döljs som standard men kan visas igen genom
              att bocka i "Visa inaktiva".
            </p>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDeactivateModal(false);
                  setDeactivatingShareholder(null);
                }}
              >
                Avbryt
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={confirmDeactivate}
                disabled={deleteShareholder.isPending}
              >
                {deleteShareholder.isPending ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Avaktiverar...
                  </>
                ) : (
                  "Avaktivera"
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
